import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listRecordings,
  uploadRecording,
  deleteRecording,
  setRecordingNickname,
  fetchRecordingAudioUrl,
} from "../api/recordings";

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_SECONDS = 52;

/** Seconds, as an operator reads them.
 *
 * Seconds are what matters here — a prompt that runs 32s against a 30s dial
 * timeout is the kind of thing this column exists to make obvious — so the
 * raw figure is always shown, with m:ss alongside once it is long enough for
 * that to be easier to hold in your head.
 */
function fmtDuration(secs: number | null): string {
  if (secs == null) return "—";
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${Math.round(secs)}s (${m}:${String(s).padStart(2, "0")})`;
}

export function RecordingsPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  async function togglePlay(filename: string) {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing === filename) {
      audio.pause();
      setPlaying(null);
      return;
    }
    try {
      const url = await fetchRecordingAudioUrl(filename);
      audio.src = url;
      await audio.play();
      setPlaying(filename);
    } catch {
      setPlaying(null);
    }
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["recordings"],
    queryFn: listRecordings,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["recordings"] });
  const upload = useMutation({
    mutationFn: ({ file, nickname }: { file: File; nickname: string }) =>
      uploadRecording(file, nickname),
    onSuccess: () => {
      setUploadError(null);
      if (fileRef.current) fileRef.current.value = "";
      invalidate();
    },
    onError: (e: unknown) => {
      // Surface the server's reason (e.g. the 45-second limit).
      const ax = e as { response?: { data?: { error?: string; detail?: string } }; message?: string };
      setUploadError(ax.response?.data?.error || ax.response?.data?.detail || ax.message || "Upload failed");
    },
  });
  const remove = useMutation({
    mutationFn: (filename: string) => deleteRecording(filename),
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: ({ filename, nickname }: { filename: string; nickname: string }) =>
      setRecordingNickname(filename, nickname),
    onSuccess: invalidate,
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setUploadError(null);
      const nickname = prompt(`Nickname for "${file.name}" (optional):`) ?? "";
      upload.mutate({ file, nickname });
    }
  }

  const recordings = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Recordings</h1>
        <div className="text-right">
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,video/*,.wav,.mp3,.gsm,.ulaw,.alaw,.m4a,.aac,.ogg,.opus,.flac,.wma,.amr,.aiff"
            className="hidden"
            onChange={onPick}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            {upload.isPending ? "Uploading…" : "Upload greeting"}
          </button>
          <p className="text-[11px] text-gray-400 mt-1">
            Max {MAX_SECONDS} seconds · any audio format
          </p>
        </div>
      </div>

      {uploadError && (
        <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2 mb-3">{uploadError}</p>
      )}

      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />


      {isLoading ? (
        <p className="text-gray-500">Loading recordings…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : recordings.length === 0 ? (
        <p className="text-gray-500">No recordings yet. Upload a greeting to get started.</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">File</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2 text-right">Length</th>
                <th className="px-4 py-2 text-right">Size</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recordings.map((r) => (
                <tr key={r.filename}>
                  <td className="px-4 py-2 font-medium">
                    {r.nickname || <span className="text-gray-400">— unnamed —</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.filename}</td>
                  <td className="px-4 py-2 uppercase text-gray-600">
                    {r.extension.replace(".", "")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {fmtDuration(r.duration_seconds)}
                  </td>
                  <td className="px-4 py-2 text-right">{fmtBytes(r.size_bytes)}</td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => togglePlay(r.filename)} className="text-brand hover:underline">
                      {playing === r.filename ? "■ Stop" : "▶ Play"}
                    </button>
                    <button
                      onClick={() => {
                        const nickname = prompt("Nickname for this greeting:", r.nickname ?? "");
                        if (nickname !== null) rename.mutate({ filename: r.filename, nickname });
                      }}
                      className="text-brand hover:underline"
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete recording "${r.nickname || r.filename}"?`)) remove.mutate(r.filename);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
